/**
 * Skins that are rigged models rather than drawings.
 *
 * A character off Meshy comes as a skinned mesh on a Mixamo skeleton with
 * a clip per file; `scripts/merge-clips.mjs` puts the clips a character
 * needs into one file and `scripts/shrink-glb.mjs` leaves it the colour
 * picture alone. Loaded once, up front, like the held models; every body
 * that wears such a skin gets a clone of the loaded scene with its own
 * skeleton, and the drawing the skin also has stays for what a model
 * cannot do yet - the arm in the corner of the eye, the picture in the
 * skin picker. "Зміни скін мавки на об'єкт."
 *
 * Unlit, like the drawn bodies: the game paints nothing with light, and a
 * lit girl among flat-shaded Cossacks is a photograph in a painting.
 */

import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as cloneSkeleton } from "three/examples/jsm/utils/SkeletonUtils.js";

/** What a rigged skin brings: its file and which clip is which. */
export interface SkinModelSpec {
  readonly url: string;
  readonly clips: { readonly idle: string; readonly walk: string; readonly run: string };
  /** Bones the game hangs things on, by name. */
  readonly bones: { readonly hand: string; readonly offHand: string; readonly head: string; readonly arm: string };
}

const MODELS: Readonly<Record<string, SkinModelSpec>> = {
  mavka: {
    url: "/models/mavka.glb",
    clips: { idle: "Idle_11", walk: "Walking", run: "Running" },
    bones: {
      hand: "mixamorig:RightHand", offHand: "mixamorig:LeftHand",
      head: "mixamorig:Head", arm: "mixamorig:RightArm",
    },
  },
  // "А ось нічний злий скін": the same rig, a different girl.
  mavkaNight: {
    url: "/models/mavka-night.glb",
    clips: { idle: "Idle_9", walk: "Walking", run: "Running" },
    bones: {
      hand: "mixamorig:RightHand", offHand: "mixamorig:LeftHand",
      head: "mixamorig:Head", arm: "mixamorig:RightArm",
    },
  },
};

interface Loaded {
  readonly scene: THREE.Group;
  readonly clips: THREE.AnimationClip[];
  readonly spec: SkinModelSpec;
  /** How tall the model stands, in its own units, feet at zero. */
  readonly height: number;
}

const loaded = new Map<string, Loaded>();
let generation = 0;
let loading: Promise<void> | null = null;

/** Bring every rigged skin in. Once; a model that will not load is left out. */
export function preloadSkinModels(): Promise<void> {
  loading ??= Promise.all(Object.entries(MODELS).map(([id, spec]) =>
    new Promise<void>((resolve) => {
      new GLTFLoader().load(spec.url, (gltf) => {
        unlight(gltf.scene);
        const box = new THREE.Box3().setFromObject(gltf.scene);
        loaded.set(id, {
          scene: gltf.scene, clips: gltf.animations, spec,
          height: Math.max(0.01, box.max.y - box.min.y),
        });
        generation++;
        resolve();
      }, undefined, () => resolve());
    }))).then(() => undefined);
  return loading;
}

/** Goes up each time a model arrives; a body that remembers it can refresh. */
export function skinModelGeneration(): number {
  return generation;
}

/** Whether the skin is one with a model at all, loaded or not. */
export function hasSkinModel(id: string): boolean {
  return MODELS[id] !== undefined;
}

/** A body of one's own for this skin, or null if it has none (yet). */
export function skinModel(id: string): SkinModelInstance | null {
  const it = loaded.get(id);
  if (it === undefined) return null;
  const root = cloneSkeleton(it.scene) as THREE.Group;
  const materials: THREE.MeshBasicMaterial[] = [];
  root.traverse((o) => {
    if (o instanceof THREE.Mesh) {
      // Cloned by SkeletonUtils with the material shared; each body wants
      // its own, so that a hurt one flashes alone.
      const material = (o.material as THREE.MeshBasicMaterial).clone();
      o.material = material;
      materials.push(material);
      o.frustumCulled = false;
      o.userData.sharedGeometry = true;
    }
  });
  // The loader strips a name of its colons and dots ("mixamorig:Head" comes
  // out "mixamorigHead"), so the spec's names are compared the same way.
  const plain = (name: string): string => name.replace(/[\[\]\.:\/\s]/g, "");
  const bone = (name: string): THREE.Object3D | null => {
    let found: THREE.Object3D | null = null;
    const want = plain(name);
    root.traverse((o) => { if (found === null && plain(o.name) === want) found = o; });
    return found;
  };
  return {
    root, materials, clips: it.clips, spec: it.spec, height: it.height,
    hand: bone(it.spec.bones.hand), offHand: bone(it.spec.bones.offHand),
    head: bone(it.spec.bones.head), arm: bone(it.spec.bones.arm),
  };
}

export interface SkinModelInstance {
  readonly root: THREE.Group;
  readonly materials: THREE.MeshBasicMaterial[];
  readonly clips: THREE.AnimationClip[];
  readonly spec: SkinModelSpec;
  readonly height: number;
  readonly hand: THREE.Object3D | null;
  readonly offHand: THREE.Object3D | null;
  readonly head: THREE.Object3D | null;
  readonly arm: THREE.Object3D | null;
}

/** The base colour alone, the way every drawn thing here is painted. */
function unlight(scene: THREE.Object3D): void {
  scene.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const was = o.material as THREE.MeshStandardMaterial;
    o.material = new THREE.MeshBasicMaterial({
      map: was.map ?? null,
      color: was.color ?? new THREE.Color(0xffffff),
      side: THREE.DoubleSide,
    });
  });
}
